import React, {useState, useEffect} from "react";
import {MapContainer, TileLayer, Marker, Popup, useMap} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import exifr from "exifr";
import Modal from "react-modal";
import L from "leaflet";
import "./PhotoMap.css";

Modal.setAppElement("#root");

// Required build-time configuration. There is deliberately no fallback value
// here: react-scripts inlines REACT_APP_* variables at build time, and
// scripts/check-required-env.js already stops the build when one is absent.
// This guard makes a hand-built bundle fail loudly as well, instead of
// silently degrading the geocoder.
const OPENCAGE_API_KEY = process.env.REACT_APP_OPENCAGE_API_KEY;

if (!OPENCAGE_API_KEY) {
    throw new Error(
        "REACT_APP_OPENCAGE_API_KEY is not set. It is required at build time " +
        "and has no default; see .env.example and scripts/check-required-env.js."
    );
}

// Optional: when present, the map keeps the CARTO Voyager style. When absent,
// getTiles() falls back to the standard OpenStreetMap tiles.
const CARTO_API_KEY = process.env.REACT_APP_CARTO_API_KEY;

// Geocoding cache
const geocodingCache = new Map();

// Component that handles automatic zooming
const AutoFitBounds = ({ photos, shouldFit }) => {
    const map = useMap();

    useEffect(() => {
        if (shouldFit && photos.length > 0) {
            const bounds = L.latLngBounds(
                photos.map(photo => [photo.lat, photo.lng])
            );
            map.fitBounds(bounds, {
                padding: [50, 50],
                maxZoom: 15
            });
        }
    }, [photos, map, shouldFit]);

    return null;
};

const PhotoMap = ({photoFolder}) => {
    const [photos, setPhotos] = useState([]);
    const [selectedPhoto, setSelectedPhoto] = useState(null);

    const [isOpen, setIsOpen] = useState(false);
    const [lovePhotos, setLovePhotos] = useState([]);
    const [placeNames, setPlaceNames] = useState(new Map());
    const [isGhibliStyle, setIsGhibliStyle] = useState(false);
    const [shouldFitBounds, setShouldFitBounds] = useState(true);

    // Read the browser language to localise the map
    const getUserLanguage = () => {
        const lang = navigator.language || navigator.languages[0] || 'en';
        console.log('Detected browser language:', lang);
        return lang;
    };

    // CARTO now enforces an API key on its raster basemaps: without one the
    // tile endpoints answer with an "API KEY REQUIRED" placeholder image, not
    // a map. When no key is configured, fall back to the standard OpenStreetMap
    // raster tiles, which need no key.
    const getTiles = () => {
        if (CARTO_API_KEY) {
            return {
                url: `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?apikey=${CARTO_API_KEY}`,
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | &copy; <a href="https://carto.com/attributions">CARTO</a>',
            };
        }
        return {
            url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        };
    };
    const tiles = getTiles();

    const getLocationName = async (lat, lng) => {
        const cacheKey = `${lat},${lng}`;
        if (geocodingCache.has(cacheKey)) {
            return geocodingCache.get(cacheKey);
        }

        // If a load for these coordinates is already running, do not start another
        if (placeNames.has(cacheKey)) {
            return placeNames.get(cacheKey);
        }

        // Set a temporary value
        setPlaceNames(prev => new Map(prev).set(cacheKey, "Загрузка..."));

        const userLang = getUserLanguage().split('-')[0]; // Получаем основной язык (ru, en, etc.)
        console.log('Using language for geocoding:', userLang);
        
        // Create a promise with a timeout
        const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Timeout')), 5000)
        );

        try {
            // Try Nominatim first (free and better language support)
            const osmPromise = fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=${userLang}&addressdetails=1&namedetails=1`
            ).then(res => res.json());

            const osmData = await Promise.race([osmPromise, timeoutPromise]);
            console.log('Nominatim response:', osmData);
            
            if (osmData && osmData.display_name) {
                const result = osmData.display_name;
                geocodingCache.set(cacheKey, result);
                setPlaceNames(prev => new Map(prev).set(cacheKey, result));
                return result;
            }
            throw new Error('No results from Nominatim');
        } catch (error) {
            console.log('Nominatim failed, trying OpenCage:', error);
            try {
                const locationPromise = fetch(
                    `https://api.opencagedata.com/geocode/v1/json?q=${lat}+${lng}&key=${OPENCAGE_API_KEY}&language=${userLang}&pretty=1`
                ).then(res => res.json());

                const locationData = await Promise.race([locationPromise, timeoutPromise]);
                console.log('OpenCage response:', locationData);
                
                if (!locationData?.results?.length) {
                    throw new Error('No results from OpenCage');
                }
                
                const result = locationData.results[0].formatted;
                geocodingCache.set(cacheKey, result);
                setPlaceNames(prev => new Map(prev).set(cacheKey, result));
                return result;
            } catch (error) {
                console.log('Both geocoding services failed:', error);
                const result = "Неизвестное место";
                geocodingCache.set(cacheKey, result);
                setPlaceNames(prev => new Map(prev).set(cacheKey, result));
                return result;
            }
        }
    };

    const processPhoto = async (photoData, existingCoords) => {
        try {
            const exifData = await exifr.gps(`${photoFolder}/${photoData.original.full}`);
            let lat = exifData?.latitude;
            let lng = exifData?.longitude;

            if (!exifData || !lat || !lng) {
                return null;
            }

            // Offset the coordinates if they already exist
            while (existingCoords.has(`${lat},${lng}`)) {
                lat += (Math.random() - 0.5) * 0.001;
                lng += (Math.random() - 0.5) * 0.001;
            }
            existingCoords.add(`${lat},${lng}`);

            const placeName = await getLocationName(lat, lng);

            const processedPhoto = {
                src: `${photoFolder}/${isGhibliStyle ? photoData.ghibli.full : photoData.original.full}`,
                thumbnail: `${photoFolder}/${isGhibliStyle ? photoData.ghibli.thumbnail : photoData.original.thumbnail}`,
                lat,
                lng,
                placeName,
                photoData: photoData // Сохраняем все данные о фотографии
            };

            console.log('Processed photo data:', processedPhoto.photoData); // Добавляем логирование
            return processedPhoto;
        } catch (error) {
            console.error(`Error processing photo ${photoData.original.full}:`, error);
            return null;
        }
    };

    const updatePhotoPaths = (processedPhotos) => {
        return processedPhotos.map(photo => ({
            ...photo,
            src: `${photoFolder}/${isGhibliStyle ? photo.photoData.ghibli.full : photo.photoData.original.full}`,
            thumbnail: `${photoFolder}/${isGhibliStyle ? photo.photoData.ghibli.thumbnail : photo.photoData.original.thumbnail}`
        }));
    };

    const loadPhotos = async () => {
        try {
            const response = await fetch(`/media/photos.json`);
            if (!response.ok) {
                throw new Error('Failed to load photos list');
            }
            
            const data = await response.json();
            const existingCoords = new Set();
            
            const processedPhotos = await Promise.all(
                data.photos.map(photo => processPhoto(photo, existingCoords))
            );
            
            const validPhotos = processedPhotos.filter(p => p && p.lat && p.lng);
            setPhotos(validPhotos);
            setShouldFitBounds(true); // Разрешаем автомасштабирование только при первой загрузке
        } catch (error) {
            console.error('Error loading photos:', error);
        }
    };

    const loadLovePhotos = async () => {
        try {
            const response = await fetch('/media/love.is/photos.json');
            if (!response.ok) {
                throw new Error('Failed to load love photos list');
            }
            const fileList = await response.json();
            setLovePhotos(fileList.map(file => `/media/love.is/${file}`));
        } catch (error) {
            console.error('Error loading love photos:', error);
        }
    };

    useEffect(() => {
        document.title = "Машенька + Ваня = ❤️";
        document.querySelector('meta[name="description"]').setAttribute(
            "content",
            `Машенька, люблю тебя больше жизни всегда-всегда! Твой дурацкий муж Ваня!`
        );

        loadPhotos();
        loadLovePhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [photoFolder]);

    useEffect(() => {
        if (photos.length > 0) {
            // Disable auto-fit while styles change so the zoom level is not reset
            setShouldFitBounds(false); 
            const updatedPhotos = updatePhotoPaths(photos);
            setPhotos(updatedPhotos);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGhibliStyle, photoFolder]);

    return (
        <div className="app-container">
            <div className="style-switcher">
                <label className="switch">
                    <input
                        type="checkbox"
                        checked={isGhibliStyle}
                        onChange={(e) => setIsGhibliStyle(e.target.checked)}
                    />
                    <span className="slider round"></span>
                </label>
            </div>
            <div className={`map-container ${isOpen ? "modal-open" : ""}`}>
                <MapContainer
                    center={[0, 0]}
                    zoom={2}
                    style={{height: "100vh", width: "100%", zIndex: 0}}
                >
                    <TileLayer
                        url={tiles.url}
                        attribution={tiles.attribution}
                    />
                    <AutoFitBounds photos={photos} shouldFit={shouldFitBounds} />
                    {photos.map((photo, index) => (
                        <Marker key={index} position={[photo.lat, photo.lng]}
                                icon={L.divIcon({
                                    className: "custom-marker",
                                    html: `<img src="${photo.thumbnail}" style="width: 50px; height: 50px; border-radius: 8px; object-fit: cover;" />`,
                                    iconSize: [50, 50],
                                    iconAnchor: [25, 25],
                                })}
                        >
                            <Popup>
                                <div>
                                    <strong>{placeNames.get(`${photo.lat},${photo.lng}`) || "Загрузка..."}</strong>
                                    <img
                                        src={photo.src}
                                        alt="Preview"
                                        style={{width: "300px", cursor: "pointer"}}
                                        onClick={() => {
                                            console.log('Opening photo with data:', photo.photoData); // Добавляем логирование
                                            setSelectedPhoto(photo.src);

                                            setIsOpen(true);
                                        }}
                                    />
                                        {photo.photoData?.description && (
                                            <div style={{
                                                color: "white",
                                                textAlign: "center",
                                                padding: "20px",
                                                maxWidth: "800px",
                                                margin: "0 auto",
                                                whiteSpace: "pre-wrap",
                                                fontFamily: "Arial, sans-serif",
                                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                                borderRadius: "8px"
                                            }}>
                                                {photo.photoData.description}
                                            </div>
                                        )}                                    
                                </div>
                            </Popup>
                        </Marker>
                    ))}
                </MapContainer>
            </div>
            <div className="love-gallery">
                {lovePhotos.map((photo, index) => (
                    <div key={index} className="love-photo-container">
                        <img
                            src={photo}
                            alt={`Love memory ${index + 1}`}
                            className="love-photo"
                            onClick={() => {
                                setSelectedPhoto(photo);
                                setIsOpen(true);
                            }}
                        />
                    </div>
                ))}
            </div>
            {selectedPhoto && (
                <Modal
                    isOpen={isOpen}
                    onRequestClose={() => {
                        setIsOpen(false);
                        setTimeout(() => {
                            setSelectedPhoto(null);
                        }, 300);
                    }}
                    style={{
                        overlay: {
                            backgroundColor: "rgba(0, 0, 0, 0.8)",
                            cursor: "default"
                        },
                        content: {
                            border: "none",
                            background: "transparent",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: 0,
                            cursor: "default"
                        },
                    }}
                >
                    <div 
                        style={{ 
                            width: "100%", 
                            height: "100%", 
                            display: "flex", 
                            flexDirection: "column",
                            justifyContent: "center", 
                            alignItems: "center" 
                        }}
                        onClick={() => {
                            setIsOpen(false);
                            setTimeout(() => {
                                setSelectedPhoto(null);
                            }, 300);
                        }}
                    >
                        <div onClick={(e) => e.stopPropagation()}>
                            <img
                                src={selectedPhoto}
                                alt="Full Size"
                                style={{
                                    maxWidth: "90vw",
                                    maxHeight: "80vh",
                                    objectFit: "contain"
                                }}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default PhotoMap;
